import { useEffect, useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { mountStripeEmbeddedCheckout } from 'utils/stripe-embedded-checkout'

const STRIPE_CHECKOUT_MOUNT_ID = 'stripe-embedded-checkout'

type StripeCheckoutDialogProps = {
  open: boolean
  publishableKey?: string | null
  clientSecret?: string | null
  onClose: () => void
  onComplete: () => void
}

const StripeCheckoutDialog = ({
  open,
  publishableKey,
  clientSecret,
  onClose,
  onComplete,
}: StripeCheckoutDialogProps) => {
  const [ready, setReady] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    if (!open || !publishableKey || !clientSecret) {
      return
    }

    let cancelled = false
    let destroy: (() => void) | undefined
    setReady(false)
    setErrorMessage(null)

    const mount = async () => {
      try {
        destroy = await mountStripeEmbeddedCheckout({
          publishableKey,
          clientSecret,
          targetId: STRIPE_CHECKOUT_MOUNT_ID,
          onComplete: () => onCompleteRef.current(),
        })
        if (!cancelled) {
          setReady(true)
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Unable to start Stripe checkout.'
          )
        }
      }
    }

    void mount()

    return () => {
      cancelled = true
      destroy?.()
    }
  }, [clientSecret, open, publishableKey])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Subscribe
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 2 }}>
        {errorMessage ? (
          <Typography color="error">{errorMessage}</Typography>
        ) : (
          <Box sx={{ position: 'relative', minHeight: 520 }}>
            {!ready && (
              <Box
                display="flex"
                justifyContent="center"
                py={6}
                sx={{ position: 'absolute', inset: 0 }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <Box id={STRIPE_CHECKOUT_MOUNT_ID} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default StripeCheckoutDialog
